import { push } from 'connected-react-router'
import * as FileSaver from 'file-saver'
import { call, put, select, takeEvery } from 'redux-saga/effects'

import type { AppAction, AppState } from '@app/state'
import type { UpdateStatusResult } from '@app/state/intake/rest'
import { updateStatus } from '@app/state/intake/rest'

import * as Helpers from './helpers'
import * as Requests from './rest'
import * as Visit from './state'

import type { AxiosResponse } from 'axios'

function* handleGetVisitList() {
  yield takeEvery(Visit.ActionType.GetList, getList)
}

function* getList(action: AppAction) {
  if (action.type === Visit.ActionType.GetList) {
    const appState: AppState = yield select((a) => a)
    try {
      const request = Requests.getVisits()
      const response: AxiosResponse<Requests.GetVisitListResponse> = yield call(
        request,
        appState.config.apiLocation,
        appState.auth.token,
      )
      const visits = response.data.visits.map(({ visit, patient }) => {
        return Helpers.convertApiVisit(visit, patient)
      })
      yield put(Visit.actions.listRetrieved(visits))
    } catch (e) {
      console.log(e)
    }
  }
}

function* spawnLoadVisitTransitions() {
  yield takeEvery(Visit.ActionType.LoadVisit, getVisit)
  yield takeEvery(Visit.ActionType.VisitRetrieved, loadEntries)
  yield takeEvery(Visit.ActionType.EntriesRetrieved, loaded)
}

function* loadEntries() {
  yield put(Visit.actions.getEntries())
}

function* loaded() {
  yield put(Visit.actions.loadVisitSuccess())
}

function* getVisit(action: AppAction) {
  if (action.type === Visit.ActionType.LoadVisit) {
    yield put(Visit.actions.loadVisitStarted())
    const appState: AppState = yield select((a) => a)
    try {
      const request = Requests.getVisitById(action.visitId)
      const response: AxiosResponse<Requests.GetVisitResponse> = yield call(
        request,
        appState.config.apiLocation,
        appState.auth.token,
      )
      const visit = Helpers.convertApiVisit(
        response.data.visit.visit,
        response.data.visit.patient,
      )
      yield put(Visit.actions.visitRetrieved(visit))
    } catch (e) {
      yield put(Visit.actions.loadVisitFailure())
    }
  }
}

function* handleGetEntries() {
  yield takeEvery(Visit.ActionType.GetEntries, getVisitEntries)
}

function* getVisitEntries(action: AppAction) {
  if (action.type === Visit.ActionType.GetEntries) {
    const appState: AppState = yield select((a) => a)
    const request = Requests.retrieveVisitEntries(
      appState.visits.currentVisit.visit.id,
    )
    const response: AxiosResponse<Requests.RetrieveVisitEntriesResponse> = yield call(
      request,
      appState.config.apiLocation,
      appState.auth.token,
    )
    const entries = response.data.entries.map((apiEntry) =>
      Helpers.convertApiEntry(apiEntry),
    )
    yield put(Visit.actions.entriesRetrieved(entries))
  }
}

function* handleCreateVisit() {
  yield takeEvery(Visit.ActionType.CreateVisit, createVisit)
}

function* createVisit(action: AppAction) {
  if (action.type === Visit.ActionType.CreateVisit) {
    const appState: AppState = yield select((a) => a)
    yield put(Visit.actions.visitCreationStarted())
    try {
      const { patientId, definitionId, startTime, location } = action
      const request = Requests.createVisit(
        patientId,
        definitionId,
        startTime,
        location,
      )
      const response: AxiosResponse<Requests.CreateVisitResponse> = yield call(
        request,
        appState.config.apiLocation,
        appState.auth.token,
      )
      yield put(
        Visit.actions.visitCreationSucceeded(
          Helpers.convertApiVisit(response.data.visit),
        ),
      )
    } catch (e) {
      yield put(
        Visit.actions.visitCreationFailed(
          'There was an error creating this visit',
        ),
      )
    }
  }
}

function* handleVisitCreationSuccess() {
  yield takeEvery(Visit.ActionType.VisitCreationSucceeded, goToNewVisit)
}

function* goToNewVisit(action: AppAction) {
  if (action.type === Visit.ActionType.VisitCreationSucceeded) {
    yield put(push(`/clinic/visit/${action.visit.id}`))
  }
}

function* handleGetVisitDefinition() {
  yield takeEvery(Visit.ActionType.GetVisitDefinition, getVisitDefinition)
}

function* getVisitDefinition(action: AppAction) {
  if (action.type === Visit.ActionType.GetVisitDefinition) {
    const appState: AppState = yield select((a) => a)
    yield put(Visit.actions.startVisitDefinitionRetrieval())
    try {
      const request = Requests.retrieveVisitDefinition(action.visitId)
      const response: AxiosResponse<Requests.RetrieveVisitDefinitionResponse> = yield call(
        request,
        appState.config.apiLocation,
        appState.auth.token,
      )
      if (response.status === 200) {
        yield put(
          Visit.actions.getVisitDefinitionSucceeded(
            Helpers.convertApiVisitDefinition(response.data.definition),
          ),
        )
      }
    } catch (e) {
      yield put(
        Visit.actions.getVisitDefinitionFailed(
          `Could not retrieve visit definition for visit ${action.visitId}`,
        ),
      )
    }
  }
}

function* handleAddInstrumentToVisit() {
  yield takeEvery(Visit.ActionType.AddInstrumentToVisit, addInstrumentToVisit)
}

function* addInstrumentToVisit(action: AppAction) {
  if (action.type === Visit.ActionType.AddInstrumentToVisit) {
    const appState: AppState = yield select((a) => a)
    yield put(Visit.actions.startAddInstrumentToVisit())
    try {
      const request = Requests.addInstrumentToVisit(
        action.visitId,
        action.instrumentId,
      )
      const response: AxiosResponse<Requests.AddInstrumentToVisitResponse> = yield call(
        request,
        appState.config.apiLocation,
        appState.auth.token,
      )
      if (response.status === 200) {
        yield put(
          Visit.actions.getVisitDefinitionSucceeded(
            yield put(
              Visit.actions.AddInstrumentToVisitSucceeded(response.statusText),
            ),
          ),
        )
        yield put(Visit.actions.reloadVisit(action.visitId))
      }
    } catch (e) {
      yield put(
        Visit.actions.addInstrumentToVisitFailed(
          `Could not add instrument for visit ${action.visitId}`,
        ),
      )
    }
  }
}

function* handleGetDefinitionList() {
  yield takeEvery(Visit.ActionType.GetDefinitionList, retrieveDefinitionList)
}

function* retrieveDefinitionList(action: AppAction) {
  if (action.type === Visit.ActionType.GetDefinitionList) {
    const appState: AppState = yield select((a) => a)
    yield put(Visit.actions.startDefinitionListRetrieval())
    try {
      const request = Requests.retrieveDefinitionList()
      const response: AxiosResponse<Requests.RetrieveDefinitionListResponse> = yield call(
        request,
        appState.config.apiLocation,
        appState.auth.token,
      )
      if (response.status === 200) {
        const definitions = response.data.definitions.map((defn) => {
          return Helpers.convertApiVisitDefinition(defn)
        })
        yield put(Visit.actions.getDefinitionListSucceeded(definitions))
      } else {
        yield put(
          Visit.actions.getDefinitionListFailed(
            `Could not retrieve visit definition list: ${response.statusText} - ${response.data}`,
          ),
        )
      }
    } catch (e) {
      yield put(
        Visit.actions.getDefinitionListFailed(
          `Could not retrieve visit definition list: ${e}`,
        ),
      )
    }
  }
}

function* handleDownloadEntryPDF() {
  yield takeEvery(Visit.ActionType.DownloadEntryPDF, requestEntryPDF)
}

function* requestEntryPDF(action: AppAction) {
  if (action.type === Visit.ActionType.DownloadEntryPDF) {
    try {
      const appState: AppState = yield select((a) => a)
      const request = Requests.requestEntryPDF(
        action.entryId,
        action.templateId,
      )
      const response = yield call(
        request,
        appState.config.apiLocation,
        appState.auth.token,
      )

      if (!response || response.status < 200 || response.status >= 300) {
        const errorData = response.data
        const errorMessage =
          errorData?.message || 'Failed to download instrument report.'
        throw new Error(errorMessage)
      }

      const fileName = Requests.getDocumentFileName(
        response.headers['content-disposition'],
        'patient-entry',
        'pdf',
      )
      FileSaver.saveAs(response.data, fileName)

      yield put(Visit.actions.downloadEntryPDFSuccess())
    } catch (e) {
      yield put(
        Visit.actions.downloadEntryPDFFailed(
          'Failed to download instrument report.',
        ),
      )
    }
  }
}

function* updateStatusForVisit(action: AppAction) {
  if (action.type === Visit.ActionType.UpdateVisitStatus) {
    const appState: AppState = yield select((a) => a)
    try {
      const request = updateStatus(action.visitId, action.status)
      const response: AxiosResponse<UpdateStatusResult> = yield call(
        request,
        appState.config.apiLocation,
        appState.auth.token,
      )
      const visit = Helpers.convertApiVisit(response.data.visit)
      yield put(Visit.actions.updateVisitStatusSuccess(visit))
    } catch (e) {
      yield put(
        Visit.actions.updateVisitStatusFailure('unable to update visit status'),
      )
    }
  }
}

function* handleUpdateStatusForVisit() {
  yield takeEvery(Visit.ActionType.UpdateVisitStatus, updateStatusForVisit)
}

function* handleUpdateStatusForVisitSuccess() {
  yield takeEvery(Visit.ActionType.UpdateVisitStatusSuccess, function* () {
    yield put(Visit.actions.getEntries())
  })
}

function* handleUpdateVisit() {
  yield takeEvery(Visit.ActionType.UpdateVisit, updateVisit)
}

function* updateVisit(action: AppAction) {
  if (action.type === Visit.ActionType.UpdateVisit) {
    const appState: AppState = yield select((a) => a)
    const request = Requests.updateVisit(action.id, action.visit)
    try {
      const response = yield call(
        request,
        appState.config.apiLocation,
        appState.auth.token,
      )
      if (response.status === 200) {
        const visit = Helpers.convertApiVisit(response.data.visit)
        yield put(Visit.actions.updateVisitSuccess(visit))
        yield put(Visit.actions.reloadVisit(visit.id))
      } else {
        yield put(
          Visit.actions.updateVisitFailure(
            `Error when updating visit ${action.id}: ${response.statusText} - ${response.data}`,
          ),
        )
      }
    } catch (e) {
      yield put(
        Visit.actions.updateVisitFailure(`Could not update visit ${action.id}`),
      )
    }
  }
}

function* handleReloadVisit() {
  yield takeEvery(Visit.ActionType.ReloadVisit, reloadVisit)
}

function* reloadVisit(action: AppAction) {
  if (action.type === Visit.ActionType.ReloadVisit) {
    yield put(Visit.actions.reloadVisitBegin())
    yield put(Visit.actions.loadVisit(action.id))
    yield put(Visit.actions.getVisitDefinition(action.id))
    yield put(Visit.actions.getDefinitionList())
  }
}

function* handleUpdateState() {
  yield takeEvery(Visit.ActionType.UpdateVisitState, updateState)
}

const updateVisitFailureMessage = (visitId, statusText, responseData) =>
  `Error when updating visit state for : ${visitId} : ${statusText} - ${responseData}`

function* updateState(action: AppAction) {
  if (action.type === Visit.ActionType.UpdateVisitState) {
    const appState: AppState = yield select((a) => a)
    const request = Requests.updateState(action.visitId, action.state)
    try {
      const response = yield call(
        request,
        appState.config.apiLocation,
        appState.auth.token,
      )
      if (response.status === 200) {
        const visit = Helpers.convertApiVisit(response.data.visit)
        yield put(Visit.actions.updateVisitStateSuccess(visit))
      } else {
        yield put(
          Visit.actions.updateVisitStateFailure(
            updateVisitFailureMessage(
              action.visitId,
              response.statusText,
              response.data,
            ),
          ),
        )
      }
    } catch (e) {
      yield put(
        Visit.actions.updateVisitStateFailure(
          `Error when updating visit state for : ${action.visitId}`,
        ),
      )
    }
  }
}

export const sagas = {
  spawnLoadVisitTransitions,
  handleAddInstrumentToVisit,
  handleGetVisitList,
  handleCreateVisit,
  handleGetVisitDefinition,
  handleGetDefinitionList,
  handleGetEntries,
  handleDownloadEntryPDF,
  handleVisitCreationSuccess,
  handleUpdateVisit,
  handleUpdateStatusForVisit,
  handleUpdateStatusForVisitSuccess,
  handleReloadVisit,
  handleUpdateState,
}
